import { useState } from "react";
import { Check, X, Info, ChevronDown, ChevronUp, Plus, Minus } from "lucide-react";
import { Button } from "../../ui/button";

const PricingCard = ({ plan, delay = 0, currency, isYearly }) => {
  const [staffCount, setStaffCount] = useState(0);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const YEARLY_DISCOUNT = 0.2;

  const getBorderColor = (color) => {
    const colors = {
      free: "border-gray-200",
      startup: "border-blue-200",
      premium: "border-purple-200",
    };
    return colors[color] || colors.free;
  };

  const getBackgroundColor = (color) => {
    const colors = {
      free: "bg-gray-50",
      startup: "bg-blue-50",
      premium: "bg-purple-50",
    };
    return colors[color] || colors.free;
  };

  const getTeamPrice = () => {
    if (plan.name === "Free") return 0;
    return currency === 'INR' ? plan.team_inr : plan.team_usd;
  };

  const getStaffPrice = () => {
    if (plan.name === "Free") return 0;
    return currency === 'INR' ? plan.staff_inr : plan.staff_usd;
  };

  const calculateTotalPrice = () => {
    if (plan.name === "Free") return 0;
    
    const teamPrice = getTeamPrice();
    const staffPrice = getStaffPrice();
    let total = teamPrice + (staffPrice * staffCount);
    
    if (isYearly) {
      total = total * 12 * (1 - YEARLY_DISCOUNT);
    }
    
    return total;
  };

  const formatPrice = (price) => {
    if (currency === 'INR') {
      return `₹${price.toLocaleString('en-IN')}`;
    } else {
      return `$${price.toFixed(2)}`;
    }
  };

  const isFree = plan.name === "Free";
  const visibleFeatures = showAllFeatures ? plan.features : plan.features.slice(0, 4);
  const totalPrice = calculateTotalPrice();

  return (
    <div
      className={`relative bg-white rounded-2xl border-2 ${getBorderColor(
        plan.color
      )} transition-all duration-300 hover:scale-105 hover:shadow-xl animate-slide-up`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {plan.name === "Premium" && (
        <div className="absolute -top-5 left-1/2 z-30 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg border-2 border-white">
            Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div className={`px-6 py-4 rounded-t-2xl ${getBackgroundColor(plan.color)}`}>
        <h3 className="text-2xl font-bold text-gray-800 text-center">
          {plan.name}
        </h3>
        <p className="text-sm text-gray-600 text-center mt-1">
          {plan.description}
        </p>
      </div>

      {/* Pricing */}
      <div className="px-6 pt-6">
        <div className="text-center mb-4">
          <div className="text-4xl font-bold text-gray-900 mb-2">
            {isFree ? "Free" : formatPrice(totalPrice)}
          </div>
          {!isFree && (
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                Team: {formatPrice(getTeamPrice())}{isYearly ? '/year' : '/month'}
              </p>
              <p className="text-sm text-gray-500">
                Staff: {formatPrice(getStaffPrice())}{isYearly ? '/year' : '/month'} each
              </p>
              {isYearly && (
                <p className="text-xs text-green-600 font-medium">
                  20% discount applied
                </p>
              )}
            </div>
          )}
        </div>

        {/* Staff Counter */}
        {!isFree && (
          <div className="flex justify-center items-center mb-6 space-x-4 bg-gray-50 rounded-lg p-3">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setStaffCount(Math.max(0, staffCount - 1))}
              disabled={staffCount === 0}
            >
              <Minus className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">{staffCount}</div>
              <div className="text-xs text-gray-500">Service Providers</div>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setStaffCount(staffCount + 1)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* CTA */}
        <Button
          className={`w-full py-3 text-lg font-semibold transition-all duration-300 mb-6 ${
            plan.name === "Premium"
              ? "bg-purple-600 hover:bg-purple-700 text-white"
              : plan.name === "Startup"
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-gray-600 hover:bg-gray-700 text-white"
          }`}
        >
          {plan.cta}
        </Button>
      </div>

      {/* Features */}
      <div className="px-6 pb-6 space-y-3">
        <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
          Features Included
        </h4>
        {visibleFeatures.map((feature, index) => (
          <div key={index} className="flex items-start space-x-3">
            <div className="mt-0.5">
              {feature.included ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <X className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <span
                  className={`text-sm ${
                    feature.included ? "text-gray-900" : "text-gray-500 line-through"
                  }`}
                >
                  {feature.name}
                </span>
                {feature.tooltip && (
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                )}
              </div>
            </div>
          </div>
        ))}
        {plan.features.length > 4 && (
          <button
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="flex items-center text-sm text-blue-600 hover:underline mt-3"
          >
            {showAllFeatures ? (
              <>
                Show less <ChevronUp className="ml-1 w-4 h-4" />
              </>
            ) : (
              <>
                Show more ({plan.features.length - 4} more features) <ChevronDown className="ml-1 w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PricingCard;